import { Link, usePage, router } from '@inertiajs/react'
import { PageProps } from '@/types'
import PendingEquityWarning from '@/components/PendingEquityWarning'
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  DollarSign, 
  Award, 
  Shield, 
  Video, 
  Settings, 
  LogOut,
  Menu,
  X,
  ChevronDown,
  ScrollText,
  FileSignature,
  UserPlus,
  Building2,
  Mail,
  MailSearch,
  GitBranch,
  ArrowLeftCircle,
  Eye,
  ClipboardList,
  BarChart3,
  Banknote,
  UserX,
  UserCog
} from 'lucide-react'
import { useState } from 'react'
import clsx from 'clsx'

interface Props {
  children: React.ReactNode
}

export default function AppLayout({ children }: Props) {
  const { auth, flash, impersonating, unsigned_documents_reminder, pending_equity_warning, pending_commissions_count, pending_payments_count } = usePage<PageProps>().props
  const pendingCount = pending_commissions_count ?? 0
  const pendingPaymentsCount = pending_payments_count ?? 0
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : ''
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  const isAdmin = auth.user && ['administrator', 'owner', 'staff'].includes(auth.user.role)
  const showUnsignedDocsBanner = !!unsigned_documents_reminder

  const isOwnerOrAdmin = auth.user && ['administrator', 'owner'].includes(auth.user.role)
  const trainingCenterVisible = auth.organization?.feature_flags?.training_center_visible ?? false
  // Owners and administrators always see money. Everyone else loses every
  // commissions/equity surface once the organization turns the setting on.
  const canViewMoney = !!isOwnerOrAdmin || !(auth.organization?.feature_flags?.hide_commissions_and_equity ?? false)

  // A person's role and their agent record are independent, and a promotion
  // deliberately keeps the record — it is their place in the commission
  // hierarchy. So an administrator can also be an agent, with their own
  // commissions, licenses and downline. The pages below already serve them
  // (they gate on having an agent record, not on the role); only the sidebar
  // used to forget, leaving the links reachable by typed URL alone.
  const hasAgentRecord = !!auth.agent

  const personalNavigation = [
    ...(canViewMoney ? [
      { name: 'My Commissions', href: '/my/commissions', icon: DollarSign },
      { name: 'My Equity', href: '/my/equity', icon: Award },
    ] : []),
    { name: 'My Licenses', href: '/my/licenses', icon: Shield },
    // Admins already carry a Training link in the admin section, and repeating
    // it here would render the same destination twice.
    ...(trainingCenterVisible && !isAdmin ? [{ name: 'Training', href: '/training', icon: Video }] : []),
    { type: 'heading' as const, name: 'My Downline' },
    { name: 'My Referrals', href: '/referrals', icon: UserPlus },
    { name: 'My Team', href: '/my/team', icon: Users },
    { name: 'My Downline Tree', href: '/my/downline_tree', icon: GitBranch },
  ]

  const navigation = isAdmin ? [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Agents', href: '/agents', icon: Users },
    { name: 'Onboarding', href: '/onboarding', icon: FileText },
    { name: 'Documents', href: '/documents', icon: FileSignature },
    ...(canViewMoney ? [
      { name: 'Commissions', href: '/commissions', icon: DollarSign },
      { name: 'Pending Commissions', href: '/admin/pending_commissions', icon: ClipboardList, badgeCount: pendingCount },
      { name: 'Payments', href: '/admin/payments', icon: Banknote, badgeCount: pendingPaymentsCount },
      { name: 'Equity', href: '/equity_credits', icon: Award },
    ] : []),
    ...(isOwnerOrAdmin ? [{ name: 'Reports', href: '/admin/reports', icon: BarChart3 }] : []),
    { name: 'Licenses', href: '/licenses', icon: Shield },
    { name: 'Training', href: '/training', icon: Video },
    // Carriers is commission configuration (payout %, split schedule), so it
    // follows the same visibility rule as the other money pages.
    ...(canViewMoney ? [{ name: 'Carriers', href: '/carriers', icon: Building2 }] : []),
    { name: 'Agent Tree', href: '/admin/agent_tree', icon: GitBranch },
    { name: 'Referrals', href: '/admin/referrals', icon: UserPlus },
    { name: 'Messaging', href: '/admin/messages', icon: Mail },
    ...(isOwnerOrAdmin ? [{ name: 'Email Activity', href: '/admin/email_activity', icon: MailSearch }] : []),
    { name: 'Settings', href: '/settings', icon: Settings },
    ...(isOwnerOrAdmin ? [{ name: 'Users', href: '/admin/users', icon: UserCog }] : []),
    { name: 'Orphan Users', href: '/admin/orphan_users', icon: UserX },
    ...(isOwnerOrAdmin ? [{ name: 'Activity Logs', href: '/activity_logs', icon: ScrollText }] : []),
    // Below the admin links, not above them: administering the organization is
    // still the primary job of this account.
    ...(hasAgentRecord ? [
      { type: 'heading' as const, name: 'My Agent Account' },
      ...personalNavigation,
    ] : []),
  ] : [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    ...personalNavigation,
  ]

  return (
    <div className="min-h-screen bg-chestnut-cream">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={clsx(
        'fixed top-0 left-0 z-50 h-full w-64 bg-chestnut-forest transform transition-transform duration-200 ease-in-out lg:translate-x-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
            <Link href="/dashboard" className="flex items-center">
              <span className="text-xl font-heading font-bold text-chestnut-cream">
                Chestnut Capital
              </span>
            </Link>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-chestnut-cream/80 hover:text-chestnut-cream"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              if ('type' in item && item.type === 'heading') {
                return (
                  <div
                    key={`heading-${item.name}`}
                    className="pt-4 pb-1 px-3 text-xs font-semibold uppercase tracking-wider text-chestnut-cream/50"
                  >
                    {item.name}
                  </div>
                )
              }
              const navItem = item as { name: string; href: string; icon: typeof LayoutDashboard; badgeCount?: number }
              const isActive = currentPath === navItem.href || currentPath.startsWith(navItem.href + '/')
              const badgeCount = navItem.badgeCount ?? 0
              return (
                <Link
                  key={navItem.name}
                  href={navItem.href}
                  className={clsx(
                    'nav-link flex items-center gap-3',
                    isActive && 'bg-white/10 text-chestnut-cream'
                  )}
                >
                  <navItem.icon className="h-5 w-5" />
                  <span className="flex-1">{navItem.name}</span>
                  {badgeCount > 0 && (
                    <span className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-semibold rounded-full bg-chestnut-gold text-chestnut-forest">
                      {badgeCount}
                    </span>
                  )}
                </Link>
              )
            })}
          </nav>

          {/* User section */}
          <div className="border-t border-white/10 p-4">
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-chestnut-cream truncate">
                  {auth.user?.first_name} {auth.user?.last_name}
                </p>
                <p className="text-xs text-chestnut-cream/60 truncate">
                  {auth.user?.email}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Impersonation banner */}
        {impersonating && (
          <div className="sticky top-0 z-40 bg-amber-500 text-white">
            <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 py-3">
              <div className="flex items-center gap-3">
                <Eye className="h-5 w-5 flex-shrink-0" />
                <span className="font-semibold text-sm sm:text-base">
                  Viewing as <span className="underline">{impersonating.agent_name}</span>
                </span>
              </div>
              <button
                onClick={() => router.delete('/admin/impersonate')}
                className="flex items-center gap-2 bg-white text-amber-700 font-bold px-4 py-2 rounded-lg hover:bg-amber-50 transition-colors text-sm sm:text-base shadow-sm"
              >
                <ArrowLeftCircle className="h-5 w-5" />
                Return to Admin
              </button>
            </div>
          </div>
        )}

        {/* Unsigned onboarding documents banner (agents only) */}
        {showUnsignedDocsBanner && (
          <div className={clsx(
            'sticky z-40 bg-chestnut-burgundy text-white',
            impersonating ? 'top-[52px]' : 'top-0'
          )}>
            <div className="flex items-center gap-3 px-4 sm:px-6 lg:px-8 py-3">
              <FileSignature className="h-5 w-5 flex-shrink-0" />
              <span className="font-semibold text-sm sm:text-base">
                You have onboarding documents waiting for your signature. Please check your email and sign them to complete your onboarding.
              </span>
            </div>
          </div>
        )}

        {/* Top bar */}
        <header className={clsx(
          "sticky z-30 bg-chestnut-cream border-b border-chestnut-gray",
          impersonating && showUnsignedDocsBanner
            ? "top-[104px]"
            : impersonating || showUnsignedDocsBanner
              ? "top-[52px]"
              : "top-0"
        )}>
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-chestnut-charcoal"
            >
              <Menu className="h-6 w-6" />
            </button>

            <div className="flex-1" />

            {/* User dropdown */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 text-sm text-chestnut-charcoal hover:text-chestnut-forest"
              >
                <span>{auth.user?.first_name} {auth.user?.last_name}</span>
                <ChevronDown className="h-4 w-4" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-chestnut-gray py-1">
                  <Link
                    href="/profile"
                    className="block px-4 py-2 text-sm text-chestnut-charcoal hover:bg-chestnut-cream"
                  >
                    Profile Settings
                  </Link>
                  <Link
                    href="/logout"
                    method="delete"
                    as="button"
                    className="w-full text-left px-4 py-2 text-sm text-chestnut-charcoal hover:bg-chestnut-cream flex items-center gap-2"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </Link>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Flash messages */}
        {flash.notice && (
          <div className="mx-4 sm:mx-6 lg:mx-8 mt-4 p-4 bg-status-success/10 border border-status-success/20 rounded-lg text-status-success">
            {flash.notice}
          </div>
        )}
        {flash.alert && (
          <div className="mx-4 sm:mx-6 lg:mx-8 mt-4 p-4 bg-status-warning/10 border border-status-warning/20 rounded-lg text-status-warning">
            {flash.alert}
          </div>
        )}
        {flash.error && (
          <div className="mx-4 sm:mx-6 lg:mx-8 mt-4 p-4 bg-status-error/10 border border-status-error/20 rounded-lg text-status-error">
            {flash.error}
          </div>
        )}

        {/* Agreement / pending equity warning (agent-only, disappears once signed) */}
        {pending_equity_warning && (
          <div className="mx-4 sm:mx-6 lg:mx-8 mt-4">
            <PendingEquityWarning warning={pending_equity_warning} />
          </div>
        )}

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  )
}
